/**
 * AsyncStateView - Composable wrapper for loading/error/empty/content states.
 * Renders the appropriate state component based on the current data lifecycle.
 */

import React from 'react';
import { LoadingState } from './LoadingState';
import { ErrorState } from './ErrorState';
import { EmptyState } from './EmptyState';

interface AsyncStateViewProps<T> {
  /** Whether data is currently loading */
  loading: boolean;
  /** Error message, if any */
  error: string | null;
  /** The data (null/undefined = not yet loaded, empty array = loaded but empty) */
  data: T | null | undefined;
  /** Check if loaded data should be considered "empty" — defaults to Array.isArray + length check */
  isEmpty?: (data: T) => boolean;

  /** Loading state config */
  loadingMessage?: string;
  /** Error state config */
  onRetry: () => void;
  retryLabel: string;
  /** Empty state config */
  emptyMessage?: string;
  emptyHint?: string;
  emptyAction?: { label: string; onPress: () => void };

  /** Render function for the content when data is available and non-empty */
  children: (data: T) => React.ReactNode;

  testID?: string;
}

function defaultIsEmpty<T>(data: T): boolean {
  return Array.isArray(data) && data.length === 0;
}

export function AsyncStateView<T>({
  loading,
  error,
  data,
  isEmpty = defaultIsEmpty,
  loadingMessage,
  onRetry,
  retryLabel,
  emptyMessage,
  emptyHint,
  emptyAction,
  children,
  testID,
}: AsyncStateViewProps<T>) {
  if (loading && (data == null)) {
    return <LoadingState message={loadingMessage} testID={testID ? `${testID}-loading` : undefined} />;
  }

  if (error && (data == null)) {
    return (
      <ErrorState
        message={error}
        onRetry={onRetry}
        retryLabel={retryLabel}
        testID={testID ? `${testID}-error` : undefined}
      />
    );
  }

  if (data != null && isEmpty(data)) {
    return (
      <EmptyState
        message={emptyMessage ?? ''}
        hint={emptyHint}
        action={emptyAction}
        testID={testID ? `${testID}-empty` : undefined}
      />
    );
  }

  if (data != null) {
    return <>{children(data)}</>;
  }

  // Fallback: no data, no loading, no error — show loading
  return <LoadingState message={loadingMessage} testID={testID ? `${testID}-loading` : undefined} />;
}
